/**
 * Servicio para operaciones con imágenes en el backend (archivos en disco del front vía PRODUCT_IMAGES_DIR)
 */
import ApiService from './ApiService';

const UPLOAD_PATH = '/api/imagenes/producto';

function errorMessageFromApi(error) {
  const payload = error && error.payload;
  let msg =
    (payload && (payload.message || payload.error)) ||
    (typeof payload === 'string' ? payload : null) ||
    (error && error.message);
  if (payload && payload.detail != null && String(payload.detail).trim() !== '') {
    msg = msg ? `${msg} — ${String(payload.detail).trim()}` : String(payload.detail).trim();
  }
  return msg || 'Error al procesar la imagen';
}

/**
 * Sube una imagen al backend (PNG, JPEG o WebP; se guarda como PNG).
 * @param {File} archivo
 * @param {string|null} nombreArchivo - Nombre deseado (basename .png) o null para nombre aleatorio
 * @returns {Promise<string>} Nombre de archivo devuelto por el backend (campo imagenURL)
 */
export const subirImagen = async (archivo, nombreArchivo = null) => {
  if (!archivo) {
    throw new Error('No se seleccionó ningún archivo');
  }
  const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/pjpeg', 'image/webp'];
  if (!allowedTypes.includes(archivo.type)) {
    throw new Error('Solo se permiten imágenes PNG, JPEG o WebP');
  }
  const maxSize = 5 * 1024 * 1024;
  if (archivo.size > maxSize) {
    throw new Error('La imagen no puede superar los 5MB');
  }

  const formData = new FormData();
  formData.append('imagen', archivo);
  if (nombreArchivo != null && String(nombreArchivo).trim() !== '') {
    formData.append('nombreArchivo', String(nombreArchivo).trim());
  }

  try {
    const response = await ApiService.post(UPLOAD_PATH, formData, {
      headers: {},
    });
    const imagenURL = response?.imagenURL;
    if (!imagenURL || typeof imagenURL !== 'string') {
      throw new Error('El servidor no devolvió el nombre de la imagen');
    }
    return imagenURL;
  } catch (error) {
    if (error.status === 409) {
      throw new Error(errorMessageFromApi(error));
    }
    if (error.status === 401 || error.status === 403) {
      throw new Error('Sesión expirada o sin permiso. Iniciá sesión nuevamente.');
    }
    throw new Error(errorMessageFromApi(error));
  }
};

/**
 * Elimina una imagen del backend por nombre de archivo
 * @param {string} urlImagen - URL, ruta o nombre del archivo .png
 */
export const eliminarImagen = async (urlImagen) => {
  if (!urlImagen || typeof urlImagen !== 'string') {
    throw new Error('Nombre de imagen no válido');
  }

  let nombreArchivo = urlImagen.trim();
  if (nombreArchivo.includes('http')) {
    try {
      const urlObj = new URL(nombreArchivo);
      nombreArchivo = urlObj.pathname.split('/').pop() || '';
    } catch (e) {
      throw new Error('URL de imagen no válida');
    }
  } else {
    nombreArchivo = nombreArchivo.split('/').pop() || '';
  }

  if (!nombreArchivo) {
    throw new Error('No se pudo determinar el nombre del archivo');
  }

  try {
    await ApiService.del(`/api/imagenes/producto/${encodeURIComponent(nombreArchivo)}`);
  } catch (error) {
    if (error.status === 404) {
      return;
    }
    throw new Error(errorMessageFromApi(error));
  }
};

/**
 * Extrae el nombre del archivo desde una URL o ruta
 * @param {string} url
 * @returns {string|null}
 */
export const obtenerNombreArchivoDesdeURL = (url) => {
  if (!url) return null;
  if (url.includes('http')) {
    try {
      const urlObj = new URL(url);
      const nombreArchivo = urlObj.pathname.split('/').pop();
      if (nombreArchivo && nombreArchivo !== '') {
        return decodeURIComponent(nombreArchivo);
      }
    } catch (e) {
      console.error('Error al parsear URL:', e);
    }
    return null;
  }
  return url.split('/').pop() || null;
};
